import React, { useEffect, useState, useMemo } from "react";
import withClinic from "../hooks/with_clinic";
import {
  ApolloResult,
  CheckIn,
  Clinic,
  Order,
  Practitioner as PractionerInterface,
  PractitionerRole,
  practitionerRoleDisplayName,
  Service,
} from "../store";
import { Row, Col, Typography, Empty } from "antd";
import Search from "antd/lib/input/Search";
import DateRange from "../components/date_range";
import { Form } from "antd";
import { Table, Card } from "antd";
import { addDays, format, subDays } from "date-fns";
import ButtonGroup from "antd/lib/button/button-group";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import LoadingSpinner from "../components/loading_indicator";
import Error from "../components/apollo_error";
import MultiSelect from "../components/multi_select";
import { formatMoney } from "../helpers/utils";
import { Avatar } from "antd";
import {
  getPractitionerBookingCountVar,
  getPractitionerCheckInCountVar,
  getPractitionersVar,
  getUpdateOnePractionerVar,
  get_practitioner_booking_count,
  get_practitioner_checkin_count,
  get_practitioners,
  update_one_practitioner,
} from "../graphql/practitioner";
import { get_practitioner_checkins, getPractitionerCheckInsVar, get_checkins_count } from "../graphql/checkin";
import { Link } from "react-router-dom";
import paths from "../routes/paths";
import { Button } from "antd";
import { EditOutlined, LoadingOutlined, SaveOutlined, FileExcelFilled } from "@ant-design/icons";
import { Popconfirm } from "antd";
import { message } from "antd";
import { Input } from "antd";
import { InputNumber } from "antd";
import { Switch } from "antd";
import { Select } from "antd";
import ImagUpload from "../components/image_upload";
import client from "../graphql/apollo_client";
import * as XLSX from "xlsx";
import FileSaver from "file-saver";

interface P {
  currentClinic?: Clinic;
}

interface Practitioner
  extends Omit<
    PractionerInterface,
    "services" | "leaves" | "metadata" | "phonenumber"
  > {
  username?: string;
  is_app_user: boolean;
  sort_order: number;
}

interface PractitionerMutate extends Practitioner {
  password: string;
}

const genderOption = [
  { label: "Male", value: "MALE" },
  { label: "Female", value: "FEMALE" },
];

const practitionerRoleOption: { label: string; value: PractitionerRole }[] = [
  { label: "Doctor", value: "Doctor" },
  { label: "Senior Therapist", value: "SeniorTherapist" },
  { label: "Junior Therapist", value: "JuniorTherapist" },
  { label: "Specialist", value: "Specialist" },
];

const practitionerRoleLabel = practitionerRoleDisplayName;

interface EditableCellProps extends React.HTMLAttributes<HTMLElement> {
  editing: boolean;
  dataIndex: string;
  title: any;
  inputType: "number" | "text" | "switch" | "password" | "select" | "img";
  record: PractitionerMutate;
  index: number;
  children: React.ReactNode;
}

const EditableCell: React.FC<EditableCellProps> = ({
  editing,
  dataIndex,
  title,
  inputType,
  record,
  index,
  children,
  ...restProps
}) => {
  let inputNode: any = <Input />;
  if (inputType === "number") {
    inputNode = <InputNumber />;
  }
  if (inputType === "switch") {
    inputNode = <Switch defaultChecked={record.status === "ACTIVE"} />;
  }
  if (inputType === "select") {
    const options =
      dataIndex === "sex"
        ? genderOption
        : dataIndex === "role"
        ? practitionerRoleOption
        : [];
    const defaultSelectValue =
      dataIndex === "role"
        ? (record as Practitioner).role ?? "Doctor"
        : (record as any)[dataIndex];
    inputNode = (
      <Select
        defaultValue={defaultSelectValue}
        style={{ width: dataIndex === "role" ? 160 : 120 }}
        options={options}
      />
    );
  }
  if (inputType === "password") {
    inputNode = <Input.Password />;
  }
  if (inputType === "img") {
    inputNode = <ImagUpload defaultValue={(record as any)[dataIndex]} />;
  }

  return (
    <td {...restProps}>
      {editing ? (
        <Form.Item
          name={dataIndex}
          style={{ margin: 0 }}
          rules={[
            ...(dataIndex === "username" && record.is_app_user
              ? [
                  {
                    pattern: /^[a-z0-9]+$/,
                    message:
                      "User Name must be all small letter and number only.",
                  },
                  {
                    required: true,
                    message: "User Name is required for app user.",
                  },
                ]
              : [
                  {
                    required: dataIndex === "name" ? true : false,
                    message: `Please Input ${title}!`,
                  },
                ]),
          ]}
        >
          {inputNode}
        </Form.Item>
      ) : (
        children
      )}
    </td>
  );
};

const ServicePage = (props: P) => {
  const clinicId = props.currentClinic?.id || "";
  // const clinicId = "clhobuntc0002s6019ajmqkcj";
  const [searchText, setSearchText] = useState<string | null>(null);
  const [filter, setFilter] = useState<string[]>([]);
  const [ranges, setRanges] = useState({
    startDate: subDays(new Date(), 30),
    endDate: addDays(new Date(), 15),
  });
  const [form] = Form.useForm();
  const [practitioners, setPractioners] = useState<Practitioner[]>([]);
  const [editingKey, setEditingKey] = useState("");
  const [exporting, setExporting] = useState(false);
  const [checkInsPagination, setCheckInsPagination] = useState({
    current: 1,
    pageSize: 20,
  });
  const isEditing = (record: PractitionerMutate) => record.id === editingKey;
  const variables = getPractitionersVar({
    clinicId,
    startDate: ranges.startDate,
    endDate: ranges.endDate,
    searchText,
    take: 150,
    status: filter,
  });
  const { loading, data, error, refetch } = useQuery<
    ApolloResult<"practitioners", Practitioner[]>
  >(get_practitioners, { variables });
  
  const checkInsVariables = useMemo(
    () =>
      getPractitionerCheckInsVar({
        clinicId,
        startDate: ranges.startDate,
        endDate: ranges.endDate,
        take: checkInsPagination.pageSize,
        skip: (checkInsPagination.current - 1) * checkInsPagination.pageSize,
      }),
    [clinicId, ranges.startDate, ranges.endDate, checkInsPagination.current, checkInsPagination.pageSize]
  );
  const { 
    loading: checkInsLoading, 
    data: checkInsData, 
    error: checkInsError 
  } = useQuery<ApolloResult<"checkIns", CheckIn[]>>(get_practitioner_checkins, {
    variables: checkInsVariables,
    skip: !clinicId,
  });

  const checkInsCountVariables = useMemo(
    () => ({
      where: checkInsVariables.where,
    }),
    [checkInsVariables.where]
  );
  const { data: checkInsCountData } = useQuery<{
    aggregateCheckIn: { _count: { _all: number } };
  }>(get_checkins_count, {
    variables: checkInsCountVariables,
    skip: !clinicId,
  });
  const [getBookingCount, { data: bookingData, loading: bload }] = useLazyQuery<
    ApolloResult<
      "groupByBooking",
      Array<{ _count: { _all: number }; practitioner_id: string }>
    >
  >(get_practitioner_booking_count);
  const [getCheckInCount, { data: checkInData, loading: cload }] = useLazyQuery<
    ApolloResult<
      "groupByCheckIn",
      Array<{ _count: { _all: number }; practitioner_id: string }>
    >
  >(get_practitioner_checkin_count);

  const [update, { loading: saveLoading }] = useMutation<
    ApolloResult<"updateOnePractitioner", Pick<Practitioner, "id">>
  >(update_one_practitioner);

  const checking = bload || cload;
  const bookingCount = Object.assign(
    {},
    ...(bookingData?.groupByBooking || []).map((a) => ({
      [a.practitioner_id]: a._count._all,
    }))
  );
  const checkInCount = Object.assign(
    {},
    ...(checkInData?.groupByCheckIn || []).map((a) => ({
      [a.practitioner_id]: a._count._all,
    }))
  );
  const load = (v: {
    practitionerIds: string[];
    startDate: Date;
    endDate: Date;
  }) => {
    getBookingCount({
      variables: getPractitionerBookingCountVar({ ...v, clinicId }),
    });
    getCheckInCount({
      variables: getPractitionerCheckInCountVar({ ...v, clinicId }),
    });
  };
  useEffect(() => {
    if (data?.practitioners && data.practitioners.length > 0) {
      setPractioners(data.practitioners);
      const practitionerIds = data.practitioners.map((p) => p.id);
      const { startDate, endDate } = ranges;
      load({ practitionerIds, startDate, endDate });
    }
  }, [data]);

  const save = async (r: Practitioner) => {
    // if (r.id.startsWith("new_")) {
    //   await onCreate(r);
    // } else {
    try {
      let formData = (await form.validateFields()) as any;
      const fData = { ...formData };
      const isActive = fData as { status: boolean };

      fData.status = isActive.status ? "ACTIVE" : "INACTIVE";

      if (props.currentClinic?.code == null) {
        message.error("Clinic code is empty");
        return;
      }

      const result = await update({
        variables: getUpdateOnePractionerVar(
          r.id,
          props.currentClinic?.code,
          fData
        ),
      });

      if (result.data?.updateOnePractitioner) {
        message.success("update success");
        await refetch();
      } else {
        message.error("update failed");
      }

      // }
      setEditingKey("");
      form.resetFields();
    } catch (error) {
      message.error("update failed");
      console.log("error", error);
    }
  };

  const onCancel = async (r: PractitionerMutate) => {
    if (r.id.startsWith("new_")) {
      const newList = [...practitioners];
      newList.shift();
      setPractioners(newList);
    }
    form.resetFields();
    setEditingKey("");
  };

  const edit = (r: PractitionerMutate) => {
    r.username = r.username?.split("@")[0];
    if (r.sort_order == null) {
      r.sort_order = 1;
    }
    if (r.max_booking_count == null) {
      r.max_booking_count = 1;
    }
    form.setFieldsValue({ ...r, role: r.role ?? "Doctor" });
    setEditingKey(r.id!);
  };

  if (loading) return <LoadingSpinner />;
  if (error) return <Error error={error} />;
  if (!data) return <Empty />;
  const onDateChange = (startDate: Date, endDate: Date) => {
    setRanges({ startDate, endDate });
    // Reset check-ins pagination when date range changes
    setCheckInsPagination({ current: 1, pageSize: checkInsPagination.pageSize });
    if (data?.practitioners && data.practitioners.length > 0) {
      const practitionerIds = data.practitioners.map((p) => p.id);
      load({ practitionerIds, startDate, endDate });
    }
  };

  const columns: any[] = [
    {
      title: "Photo",
      dataIndex: "image",
      key: "image",
      editable: true,
      render: (v: number, r: PractitionerMutate) => {
        return (
          <Link to={paths.getTherapistDetailRoute(r.id)}>
            <Avatar src={r.image} size={50} />
          </Link>
        );
      },
    },
    {
      title: "Display Name",
      dataIndex: "name",
      key: "name",
      width: 200,
      editable: true,
      sorter: (a: PractitionerMutate, b: PractitionerMutate) =>
        a.name.toLowerCase().localeCompare(b.name.toLowerCase()),
      render: (v: number, r: Service) => {
        return <Link to={paths.getTherapistDetailRoute(r.id)}>{r.name}</Link>;
      },
      defaultSortOrder: "ascend",
    },
    {
      title: "User Name",
      dataIndex: "username",
      key: "username",
      editable: true,
      sorter: (a: PractitionerMutate, b: PractitionerMutate) =>
        a.username
          ?.toLowerCase()
          .localeCompare(b.username?.toLowerCase() ?? ""),
    },
    {
      title: "Password",
      dataIndex: "password",
      key: "password",
      editable: true,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 110,
      sorter: (a: PractitionerMutate, b: PractitionerMutate) =>
        a.status.localeCompare(b.status),
      editable: true,
    },
    {
      title: "Booking",
      dataIndex: "booking",
      key: "booking",
      sorter: (a: PractitionerMutate, b: PractitionerMutate) =>
        (bookingCount[a.id] || 0) - (bookingCount[b.id] || 0),
      editable: false,
      render: (v: number, r: PractitionerMutate) => {
        return bookingCount[r.id] || 0;
      },
    },
    {
      title: "CheckIn",
      dataIndex: "check_in",
      key: "check_in",
      sorter: (a: PractitionerMutate, b: PractitionerMutate) =>
        (checkInCount[a.id] || 0) - (checkInCount[b.id] || 0),
      editable: false,
      render: (v: number, r: PractitionerMutate) => {
        return checkInCount[r.id] || 0;
      },
    },
    {
      title: "Gender",
      dataIndex: "sex",
      key: "sex",
      width: 100,
      editable: true,
      sorter: (a: PractitionerMutate, b: PractitionerMutate) =>
        a.sex.localeCompare(b.sex),
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      width: 180,
      editable: true,
      sorter: (a: PractitionerMutate, b: PractitionerMutate) =>
        practitionerRoleLabel(a.role).localeCompare(
          practitionerRoleLabel(b.role)
        ),
      render: (_v: PractitionerRole | undefined, r: PractitionerMutate) =>
        practitionerRoleLabel(r.role),
    },
    {
      title: "Sort Order",
      dataIndex: "sort_order",
      key: "sort_order",
      editable: true,
      sorter: (a: PractitionerMutate, b: PractitionerMutate) =>
        (a.sort_order || 1) - (b.sort_order || 1),
      render: (v: number) => v || 1,
    },
    {
      title: "Max Booking Count",
      dataIndex: "max_booking_count",
      key: "max_booking_count",
      editable: true,
      sorter: (a: PractitionerMutate, b: PractitionerMutate) =>
        (a.max_booking_count ?? 1) - (b.max_booking_count ?? 1),
      render: (v: number) => v ?? 1,
    },
    {
      title: "CreatedAt",
      dataIndex: "created_at",
      key: "created_at",
      editable: false,
      sorter: (a: Order, b: Order) =>
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
      render: (v: Order, r: Order) => {
        return format(new Date(r.created_at), "dd MMM,yyyy");
      },
    },
    {
      title: "Action",
      key: "action",
      align: "center",
      width: 96,
      dataIndex: "action",
      editable: false,
      fixed: "right",
      render: (v: number, record: PractitionerMutate) => {
        const editable = isEditing(record);
        return (
          <ButtonGroup>
            {editable ? (
              saveLoading ? (
                <Button type="primary" icon={<LoadingOutlined />}>
                  saving...
                </Button>
              ) : (
                <Popconfirm
                  placement="topRight"
                  title={`Sure to save ${record.name}?`}
                  onConfirm={() => save(record)}
                  onCancel={() => onCancel(record)}
                >
                  <Button type="primary" icon={<SaveOutlined />}>
                    save
                  </Button>
                </Popconfirm>
              )
            ) : (
              <Button
                shape="circle"
                icon={<EditOutlined />}
                onClick={() => edit(record)}
              />
            )}
          </ButtonGroup>
        );
      },
    },
  ];

  const mergedColumns = columns.map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record: PractitionerMutate) => ({
        record,
        inputType:
          col.dataIndex === "sex" || col.dataIndex === "role"
            ? "select"
            : col.dataIndex === "password"
            ? "password"
            : col.dataIndex === "status"
            ? "switch"
            : col.dataIndex === "image"
            ? "img"
            : col.dataIndex === "sort_order" || col.dataIndex === "max_booking_count"
            ? "number"
            : "text",
        dataIndex: col.dataIndex,
        title: col.title,
        editing: isEditing(record),
      }),
    };
  });

  const checkInsColumns: any[] = [
    {
      title: "Date",
      dataIndex: "in_time",
      key: "in_time",
      sorter: (a: CheckIn, b: CheckIn) =>
        new Date(a.in_time).getTime() - new Date(b.in_time).getTime(),
      render: (v: number, r: CheckIn) => {
        return format(new Date(r.in_time), "dd MMM,yyyy h:mm a");
      },
    },
    {
      title: "Therapist Name",
      dataIndex: "practitioners",
      key: "practitioners",
      sorter: (a: CheckIn, b: CheckIn) => {
        const getTherapistNames = (checkIn: CheckIn): string => {
          if (checkIn.practitioners && checkIn.practitioners.length > 0) {
            return checkIn.practitioners
              .map((p: any) => p.practitioner?.name)
              .filter((name: string | undefined) => name)
              .join(", ");
          }
          return checkIn.practitioner?.name || "";
        };
        const aNames = getTherapistNames(a);
        const bNames = getTherapistNames(b);
        return aNames.toLowerCase().localeCompare(bNames.toLowerCase());
      },
      render: (v: any, r: CheckIn) => {
        if (r.practitioners && r.practitioners.length > 0) {
          const names = r.practitioners
            .map((p: any) => p.practitioner?.name)
            .filter((name: string | undefined) => name)
            .join(", ");
          return names || r.practitioner?.name || "-";
        }
        return r.practitioner?.name || "-";
      },
    },
    {
      title: "Helper Name",
      dataIndex: "practitioners",
      key: "helper_name",
      sorter: (a: CheckIn, b: CheckIn) => {
        const getHelperNames = (checkIn: CheckIn): string => {
          if (checkIn.practitioners && checkIn.practitioners.length > 0) {
            const helpers = checkIn.practitioners
              .filter((p) => p.helper_id && p.helper)
              .map((p) => p.helper?.name)
              .filter((name): name is string => !!name)
              .join(", ");
            if (helpers) return helpers;
          }
          return checkIn.helper?.name || "";
        };
        const aNames = getHelperNames(a);
        const bNames = getHelperNames(b);
        return aNames.toLowerCase().localeCompare(bNames.toLowerCase());
      },
      render: (v: any, r: CheckIn) => {
        if (r.practitioners && r.practitioners.length > 0) {
          const helpers = r.practitioners
            .filter((p) => p.helper_id && p.helper)
            .map((p) => p.helper?.name)
            .filter((name): name is string => !!name)
            .join(", ");
          if (helpers) return helpers;
        }
        // Fallback to direct helper from check-in
        return r.helper?.name || "-";
      },
    },
    {
      title: "Service",
      dataIndex: "service",
      key: "service",
      sorter: (a: CheckIn, b: CheckIn) =>
        (a.service?.name || "")
          .toLowerCase()
          .localeCompare((b.service?.name || "").toLowerCase()),
      render: (v: any, r: CheckIn) => {
        return r.service?.name || "-";
      },
    },
    {
      title: "Customer",
      dataIndex: "member",
      key: "member",
      sorter: (a: CheckIn, b: CheckIn) =>
        (a.member?.name || "")
          .toLowerCase()
          .localeCompare((b.member?.name || "").toLowerCase()),
      render: (v: any, r: CheckIn) => {
        if (r.member?.clinic_members && r.member.clinic_members.length > 0) {
          return r.member.clinic_members[0].name;
        }
        return r.member?.name || "-";
      },
    },
  ];

  const handleExport = async () => {
    const fileName =
      "therapists_" +
      format(ranges.startDate, "yyyy-MMM-dd") +
      "_" +
      format(ranges.endDate, "yyyy-MMM-dd");
    let excelData: any[] = [];

    let list: Practitioner[] = [];
    let rolling = true;
    const take = 100;
    let skip = 0;
    const header = [
      "Display Name",
      "User Name",
      "Status",
      "Gender",
      "Role",
      "Booking Count",
      "CheckIn Count",
      "Created At",
    ];

    setExporting(true);
    try {
      // Fetch all practitioners in batches
      while (rolling) {
        const variables = getPractitionersVar({
          clinicId,
          startDate: ranges.startDate,
          endDate: ranges.endDate,
          searchText: null,
          take,
          status: filter.length > 0 ? filter : undefined,
        });
        variables.skip = skip;
        const result = await client.query<
          ApolloResult<"practitioners", Practitioner[]>
        >({
          query: get_practitioners,
          variables,
        });
        const pracs = result.data?.practitioners || [];
        list = list.concat(pracs);
        skip += pracs.length;
        rolling = pracs.length === take;
      }

      // Get booking and checkin counts for all practitioners
      const practitionerIds = list.map((p) => p.id);
      const { startDate, endDate } = ranges;

      const bookingCountResult = await client.query<
        ApolloResult<
          "groupByBooking",
          Array<{ _count: { _all: number }; practitioner_id: string }>
        >
      >({
        query: get_practitioner_booking_count,
        variables: getPractitionerBookingCountVar({
          clinicId,
          practitionerIds,
          startDate,
          endDate,
        }),
      });

      const checkInCountResult = await client.query<
        ApolloResult<
          "groupByCheckIn",
          Array<{ _count: { _all: number }; practitioner_id: string }>
        >
      >({
        query: get_practitioner_checkin_count,
        variables: getPractitionerCheckInCountVar({
          clinicId,
          practitionerIds,
          startDate,
          endDate,
        }),
      });

      const bookingCountMap = Object.assign(
        {},
        ...(bookingCountResult.data?.groupByBooking || []).map((a) => ({
          [a.practitioner_id]: a._count._all,
        }))
      );

      const checkInCountMap = Object.assign(
        {},
        ...(checkInCountResult.data?.groupByCheckIn || []).map((a) => ({
          [a.practitioner_id]: a._count._all,
        }))
      );

      // Format data for Excel - Therapists
      for (const i of list) {
        const displayName = i.name;
        const username = i.username?.split("@")[0] || "";
        const status = i.status;
        const gender = i.sex;
        const role = practitionerRoleLabel(i.role);
        const bookingCount = bookingCountMap[i.id] || 0;
        const checkInCount = checkInCountMap[i.id] || 0;
        const createdAt = format(new Date(i.created_at), "dd MMM,yyyy");

        excelData.push({
          display_name: displayName,
          username,
          status,
          gender,
          role,
          booking_count: bookingCount,
          checkin_count: checkInCount,
          created_at: createdAt,
        });
      }

      let checkInsList: CheckIn[] = [];
      let checkInsRolling = true;
      const checkInsTake = 100;
      let checkInsSkip = 0;
      while (checkInsRolling) {
        const checkInsVariables = getPractitionerCheckInsVar({
          clinicId,
          startDate: ranges.startDate,
          endDate: ranges.endDate,
          take: checkInsTake,
          skip: checkInsSkip,
        });
        const checkInsResult = await client.query<
          ApolloResult<"checkIns", CheckIn[]>
        >({
          query: get_practitioner_checkins,
          variables: checkInsVariables,
        });
        const checkIns = checkInsResult.data?.checkIns || [];
        checkInsList = checkInsList.concat(checkIns);
        checkInsSkip += checkIns.length;
        checkInsRolling = checkIns.length === checkInsTake;
      }

      const checkInsHeader = [
        "CheckIn Time",
        "Therapist",
        "Helper Name",
        "Member",
        "Service",
        "Booking Status",
      ];
      const checkInsExcelData = checkInsList.map((c) => {
        const checkInTime = format(new Date(c.in_time), "dd MMM,yyyy h:mm a");
        let therapist = "-";
        if (c.practitioners && c.practitioners.length > 0) {
          const names = c.practitioners
            .map((p: any) => p.practitioner?.name)
            .filter((name: string | undefined) => name)
            .join(", ");
          therapist = names || c.practitioner?.name || "-";
        } else {
          therapist = c.practitioner?.name || "-";
        }
        let helperName = "-";
        if (c.practitioners && c.practitioners.length > 0) {
          const helpers = c.practitioners
            .filter((p) => p.helper_id && p.helper)
            .map((p) => p.helper?.name)
            .filter((name): name is string => !!name)
            .join(", ");
          if (helpers) {
            helperName = helpers;
          } else {
            helperName = c.helper?.name || "-";
          }
        } else {
          helperName = c.helper?.name || "-";
        }
        const member =
          c.member?.clinic_members && c.member.clinic_members.length > 0
            ? c.member.clinic_members[0].name
            : c.member?.name || "-";
        const service = c.service?.name || "-";
        const bookingStatus = c.booking?.status || "-";

        return {
          checkin_time: checkInTime,
          therapist,
          helper_name: helperName,
          member,
          service,
          booking_status: bookingStatus,
        };
      });

      // Create worksheets
      const therapistsWs = XLSX.utils.json_to_sheet(excelData);
      const checkInsWs = XLSX.utils.json_to_sheet(checkInsExcelData);

      // Add headers
      if (header != null && header.length > 0) {
        XLSX.utils.sheet_add_aoa(therapistsWs, [header], { origin: "A1" });
      }
      if (checkInsHeader != null && checkInsHeader.length > 0) {
        XLSX.utils.sheet_add_aoa(checkInsWs, [checkInsHeader], { origin: "A1" });
      }

      // Create workbook with multiple sheets
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, therapistsWs, "Therapists");
      XLSX.utils.book_append_sheet(wb, checkInsWs, "Appointment Details");

      // Export file
      const fileType =
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
      const fileExtension = ".xlsx";
      const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      const blob = new Blob([excelBuffer], { type: fileType });
      FileSaver.saveAs(blob, fileName + fileExtension);
    } catch (error) {
      console.error("Export failed:", error);
      message.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  const control = (
    <Row style={{ marginTop: -18 }}>
      <Col flex={4}>
        <Row>
          <Col span={8}>
            <Typography level={2}>Therapists</Typography>
          </Col>
          <Col span={8} offset={8}>
            <Search
              defaultValue={searchText || undefined}
              placeholder={`search ${
                props.currentClinic?._count.practitioners || 0
              } therapists...`}
              allowClear
              size="middle"
              onSearch={(val: any) => setSearchText(val)}
            />
          </Col>
        </Row>
      </Col>
      <Col flex={0}>
        <DateRange
          startDate={ranges.startDate}
          endDate={ranges.endDate}
          onSelect={onDateChange}
        />
      </Col>
    </Row>
  );
  const therapistsTable = (
    <Card title="Therapists List" style={{ marginBottom: 16 }}>
      <Form form={form} autoComplete="off" component={false}>
        <Table
          loading={checking}
          showHeader={practitioners.length > 0}
          components={{ body: { cell: EditableCell } }}
          dataSource={practitioners.map((b) => ({ key: b.id, ...b }))}
          columns={mergedColumns}
          rowClassName="editable-row"
          pagination={false}
          scroll={{ x: 1650, y: 400 }}
        />
      </Form>
    </Card>
  );

  const handleCheckInsPaginationChange = (page: number, pageSize?: number) => {
    setCheckInsPagination({
      current: page,
      pageSize: pageSize || checkInsPagination.pageSize,
    });
  };

  const checkInsTable = (
    <Card title="Appointment Details" style={{ marginBottom: 16 }}>
      {checkInsError ? (
        <Error error={checkInsError} />
      ) : (
        <Table
          loading={checkInsLoading}
          showHeader={(checkInsData?.checkIns || []).length > 0}
          dataSource={(checkInsData?.checkIns || []).map((c) => ({
            key: c.id,
            ...c,
          }))}
          columns={checkInsColumns}
          pagination={{
            current: checkInsPagination.current,
            pageSize: checkInsPagination.pageSize,
            total: checkInsCountData?.aggregateCheckIn?._count?._all || 0,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
            onChange: handleCheckInsPaginationChange,
            onShowSizeChange: handleCheckInsPaginationChange,
          }}
        />
      )}
    </Card>
  );
  const filters = (
    <Row style={{ marginTop: 4 }}>
      <Col span={22}>
        <MultiSelect
          selected={filter}
          placeholder="Select therapist status"
          options={["ACTIVE", "INACTIVE"]}
          onChange={setFilter}
        />
      </Col>
      <Col span={2}>
        <Button
          size="small"
          loading={exporting}
          icon={<FileExcelFilled />}
          onClick={() => handleExport()}
        >
          Export
        </Button>
      </Col>
    </Row>
  );

  return (
    <React.Fragment>
      {control}
      {filters}
      <Row style={{ marginTop: 8 }}>
        <Col span={24}>
          {therapistsTable}
        </Col>
      </Row>
      <Row style={{ marginTop: 8 }}>
        <Col span={24}>
          {checkInsTable}
        </Col>
      </Row>
    </React.Fragment>
  );
};

export default withClinic(ServicePage);
